/**
 * AgenticPaymentTransactionHistory - Main Component
 *
 * Entry point for the Transaction History feature.
 * Manages the two-view flow:
 *   1. Wallet List - shows HBAR wallets with "View History" action
 *   2. Transaction History - shows categorized transactions for a selected wallet
 *
 * Wallets are fetched via server-side pagination (one page at a time).
 * Search is debounced and delegated to the server.
 * State is preserved when navigating between views.
 *
 * @component
 */

import React, { useState, useEffect, useCallback } from "react";
import { getRequest } from "../../utils/httpRequest";

// Page components
import WalletTable from "./WalletTable";
import TransactionHistory from "./TransactionHistory";

// Constants & styles
import { VIEWS, API_ENDPOINTS, WALLETS_PER_PAGE } from "./constants";
import { pageHeaderStyles, colors } from "./styles";
import "./styles.scss";

const AgenticPaymentTransactionHistory = ({ hederaNetwork = "testnet" }) => {
  // View state
  const [currentView, setCurrentView] = useState(VIEWS.WALLETS);
  const [selectedWallet, setSelectedWallet] = useState(null);

  // Wallet data, server-side paginated
  const [wallets, setWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(true);
  const [walletsMeta, setWalletsMeta] = useState({
    total: 0,
    page: 1,
    per_page: WALLETS_PER_PAGE,
    total_pages: 1,
  });

  // Search state (persisted across view toggles)
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Track the last-submitted search to avoid redundant fetches
  const [submittedQuery, setSubmittedQuery] = useState("");

  // Fetch a single page of wallets from the server
  const fetchWalletPage = useCallback(async (page = 1, query = "") => {
    setWalletsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: WALLETS_PER_PAGE.toString(),
      });
      if (query.trim()) {
        params.set("q", query.trim());
      }

      const data = await getRequest(
        `${API_ENDPOINTS.HEDERA_WALLETS}?${params.toString()}`,
      );

      if (data.success) {
        setWallets(data.data);
        setWalletsMeta(
          data.meta || {
            total: data.data.length,
            page: page,
            per_page: WALLETS_PER_PAGE,
            total_pages: 1,
          },
        );
      } else {
        setWallets([]);
        setWalletsMeta({
          total: 0,
          page: 1,
          per_page: WALLETS_PER_PAGE,
          total_pages: 1,
        });
      }
    } catch (error) {
      console.error("Error fetching hedera wallets:", error);
      setWallets([]);
    } finally {
      setWalletsLoading(false);
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchWalletPage(1, "");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle page change
  const handlePageChange = useCallback(
    (newPage) => {
      setCurrentPage(newPage);
      fetchWalletPage(newPage, searchQuery);
    },
    [fetchWalletPage, searchQuery],
  );

  // Handle search submit (button click or Enter key)
  const handleSearchSubmit = useCallback(() => {
    setSubmittedQuery(searchQuery);
    setCurrentPage(1);
    fetchWalletPage(1, searchQuery);
  }, [fetchWalletPage, searchQuery]);

  // Handle clearing search
  const handleSearchClear = useCallback(() => {
    setSearchQuery("");
    setSubmittedQuery("");
    setCurrentPage(1);
    fetchWalletPage(1, "");
  }, [fetchWalletPage]);

  // Handle viewing a wallet's transaction history
  const handleViewHistory = (wallet) => {
    setSelectedWallet(wallet);
    setCurrentView(VIEWS.TRANSACTIONS);
  };

  // Handle going back to wallets, preserves page and search state (no refetch)
  const handleBackToWallets = () => {
    setSelectedWallet(null);
    setCurrentView(VIEWS.WALLETS);
  };

  return (
    <div className="liquidity-pool-transaction-history">
      {/* Page Header */}
      <div style={pageHeaderStyles.container}>
        <h1 style={pageHeaderStyles.title}>Wallet Transaction History</h1>
        <p style={pageHeaderStyles.subtitle}>
          View categorized transaction history for Hedera wallets
        </p>
      </div>

      {/* Content */}
      <div style={{ padding: "20px 0" }}>
        {currentView === VIEWS.WALLETS ? (
          <WalletTable
            wallets={wallets}
            walletsLoading={walletsLoading}
            totalCount={walletsMeta.total}
            currentPage={currentPage}
            totalPages={walletsMeta.total_pages}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSearchSubmit={handleSearchSubmit}
            onSearchClear={handleSearchClear}
            onPageChange={handlePageChange}
            onViewHistory={handleViewHistory}
          />
        ) : (
          <TransactionHistory
            wallet={selectedWallet}
            hederaNetwork={hederaNetwork}
            onBack={handleBackToWallets}
          />
        )}
      </div>
    </div>
  );
};

export default AgenticPaymentTransactionHistory;
